/**
 * ChatLayout - Main chat interface layout
 * WhatsApp-style layout: conversation list (left) + chat window (right)
 */
import React, { useState, useEffect } from 'react';
import ConversationList from './ConversationList';
import ChatWindow from './ChatWindow';
import { chatAPI } from '../../utils/chatAPI';
import './ChatLayout.css';

const ChatLayout = ({ organizationId }) => {
  const [conversations, setConversations] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch conversations on mount
  useEffect(() => {
    fetchConversations();
    // Refresh conversations every 30 seconds
    const interval = setInterval(fetchConversations, 30000);
    return () => clearInterval(interval);
  }, [organizationId]);
  
  const fetchConversations = async () => {
    try {
      const data = await chatAPI.getConversations(organizationId);
      setConversations(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSelectUser = (user) => {
    setSelectedUser(user);
  };
  
  const handleNewMessage = () => {
    // Refresh conversations when new message is sent
    fetchConversations();
  };
  
  return (
    <div className="chat-layout">
      {/* Left Panel: Conversation List */}
      <div className="chat-sidebar">
        <div className="chat-sidebar-header">
          <h2>Messages</h2>
        </div>
        
        {loading ? (
          <div className="chat-sidebar-loading">
            <p>Loading conversations...</p>
          </div>
        ) : error ? (
          <div className="chat-sidebar-error">
            <p>{error}</p>
            <button onClick={fetchConversations}>Retry</button>
          </div>
        ) : (
          <ConversationList
            conversations={conversations}
            selectedUser={selectedUser}
            onSelectUser={handleSelectUser}
            organizationId={organizationId}
          />
        )}
      </div>
      
      {/* Right Panel: Chat Window */}
      <div className="chat-main">
        {selectedUser ? (
          <ChatWindow
            user={selectedUser}
            organizationId={organizationId}
            onNewMessage={handleNewMessage}
          />
        ) : (
          <div className="chat-placeholder">
            <div className="chat-placeholder-content">
              <svg
                className="chat-placeholder-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              <h3>Select a conversation</h3>
              <p>Choose a conversation from the list to start messaging</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatLayout;
